// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import * as Dialog from '@wandb/weave/components/Dialog';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useMemo, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {toast} from 'react-toastify';

import {useOrgTeamsMrConfigQuery} from '../../../../generated/graphql';
import {createDismissibleBanner} from '../../../../pages/Billing/AccountSettings/WarningBanner/DismissibleBanner';
import {Analytics} from '../../../../services/analytics';
import {propagateErrorsContext} from '../../../../util/errors';
import {cyrb53} from '../../../../util/hash';
import * as urls from '../../../../util/urls';
import {
  Banner,
  BannerBoldText,
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../../Banner';
import {SelectEntity} from '../../../Launch/Queues/SelectEntity';
import {checkRegistryProject} from '../../common/utils';

export type FindMigratedRegistryBannerProps = {
  orgID: string;
  orgName: string;
  userID: string;
};

export const FindMigratedRegistryBanner =
  createDismissibleBanner<FindMigratedRegistryBannerProps>({
    id: ({orgID, userID}) =>
      cyrb53(orgID + ':' + userID).toString() + ':find-migrated-gr',
    Component: ({orgName, onDismiss}) => {
      const [isModalOpen, setIsModalOpen] = useState(false);
      const [teamName, setTeamName] = useState<string>('');
      const history = useHistory();
      // For an organization, fetch the migrated registry project names for each team
      const {data, loading} = useOrgTeamsMrConfigQuery({
        variables: {
          orgName: orgName,
        },
        skip: orgName === '',
        context: propagateErrorsContext(),
      });
      const migratedRegistriesMap = useMemo(() => {
        const teams = data?.organization?.teams ?? [];
        return Object.fromEntries(
          teams
            .map(team => {
              const migratedRegistryProjectName =
                team?.modelRegistryMigrationConfig?.orgRegistryProject?.name;
              const {registryName} = checkRegistryProject(
                migratedRegistryProjectName
              );
              return [team?.name, registryName];
            })
            .filter(([_, registryName]) => registryName != null)
        );
      }, [data?.organization?.teams]);

      // If the organization has no teams with a migrated registry, do not show this banner
      // This condition will also be true for organizations whose migrations have not begun
      if (loading || Object.keys(migratedRegistriesMap).length === 0) {
        return null;
      }
      return (
        <Tailwind>
          <Banner
            data-test="find-migrated-registries-banner"
            variant={'announcement'}
            className="mt-16">
            <BannerFlexWrapper>
              <div>
                <Icon name="warning" />
              </div>
              <BannerTextWrapper>
                <span>
                  <BannerBoldText>Migration alert. </BannerBoldText>
                  One or more of your team model registries is now a custom
                  registry.
                </span>
              </BannerTextWrapper>
            </BannerFlexWrapper>
            <div className="ml-12 flex gap-12 font-semibold text-gold-700 hover:cursor-pointer">
              <button
                className="flex items-center border-none bg-transparent hover:brightness-150"
                onClick={() => {
                  setIsModalOpen(true);
                }}>
                Locate custom registry
              </button>
              <button
                className="flex items-center border-none bg-transparent hover:brightness-150"
                onClick={() => {
                  onDismiss();
                }}>
                <Icon name="close" />
              </button>
            </div>
          </Banner>

          <Dialog.Root open={isModalOpen} onOpenChange={setIsModalOpen}>
            <Dialog.Portal>
              <Dialog.Overlay />
              <Dialog.Content
                className="w-[600px] rounded-lg"
                onInteractOutside={e => {
                  e.preventDefault();
                }}>
                <Dialog.Title>Locate custom registry</Dialog.Title>
                <div className="my-24">
                  <div className="mb-4 font-semibold">Team</div>
                  <SelectEntity
                    size="large"
                    value={teamName ?? ''}
                    onSelectEntity={setTeamName}
                  />
                </div>
                <div className="mb-8 flex items-center">
                  <Dialog.Close asChild>
                    <Button
                      onClick={() => {
                        Analytics.track('Go-to-migrated-registry clicked', {
                          location: 'Find migrated registry banner modal',
                        });
                        if (migratedRegistriesMap[teamName] != null) {
                          const url = urls.globalRegistry(
                            orgName,
                            migratedRegistriesMap[teamName]
                          );
                          history.push(url);
                        } else {
                          toast(
                            'This team had no legacy model registry that needed to be migrated.'
                          );
                        }
                      }}
                      variant="primary"
                      size="large"
                      disabled={teamName === ''}>
                      Go to registry
                    </Button>
                  </Dialog.Close>
                  <Dialog.Close className="ml-8" asChild>
                    <Button
                      className="dark:text-moon-200/100 dark:filter-none hover:dark:text-moon-200"
                      size="large"
                      variant="secondary">
                      Cancel
                    </Button>
                  </Dialog.Close>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </Tailwind>
      );
    },
  });
